import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from "react";

type TTheme = "dark" | "light" | "system";

type TThemeProviderProps = {
    children: ReactNode;
    defaultTheme?: TTheme;
    storageKey?: string;
};

type TThemeProviderState = {
    theme: TTheme;
    setTheme: (theme: TTheme) => void;
};

const initialState: TThemeProviderState = {
    theme: "system",
    setTheme: () => null,
};

const ThemeProviderContext = createContext<TThemeProviderState>(initialState);

export function ThemeProvider({
    children,
    defaultTheme = "system",
    storageKey = "proda-finance-ui-theme",
}: TThemeProviderProps) {
    const [theme, setTheme] = useState<TTheme>(
        () => (localStorage.getItem(storageKey) as TTheme) || defaultTheme,
    );

    useEffect(() => {
        const root = window.document.documentElement;

        // Dark mode is temporarily disabled: always force light, ignoring the
        // stored/system preference. To restore theme switching, drop this block
        // back to honoring `theme` (light/dark/system) and re-add a toggle UI.
        root.classList.remove("light", "dark");
        root.classList.add("light");
    }, []);

    const handleSetTheme = useCallback(
        (newTheme: TTheme) => {
            localStorage.setItem(storageKey, newTheme);
            setTheme(newTheme);
        },
        [storageKey],
    );

    const value = useMemo(() => ({ theme, setTheme: handleSetTheme }), [theme, handleSetTheme]);

    return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export const useTheme = () => {
    const context = useContext(ThemeProviderContext);

    if (context === undefined) {
        throw new Error("useTheme must be used within a ThemeProvider");
    }

    return context;
};
